import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import {
    Add as AddIcon,
    Inventory as MaterialIcon,
    Remove as RemoveIcon,
    Tune as AdjustIcon,
} from "@mui/icons-material";
import MaterialService from "../services/Material.service";
import InventoryService from "../services/Inventory.service";

const TYPE_META = {
    RM: { label: "Raw Material", fetch: (id) => MaterialService.getRawMaterial(id) },
    AS: { label: "Assembly", fetch: (id) => MaterialService.getAssembly(id) },
};

const ADJUST_INITIAL = { lot: "", direction: "in", quantity: "", notes: "" };

function StatusChip({ status }) {
    const map = { active: "success", inactive: "default", draft: "warning", dev: "warning" };
    return (
        <Chip
            label={status ?? "—"}
            size="small"
            color={map[status] ?? "default"}
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
        />
    );
}

// Scan target for the QR code shown in the desktop app's item dialog:
// /shop/material?type=RM|AS&id=<id>
export default function MaterialLookup() {
    const [searchParams] = useSearchParams();
    const type = (searchParams.get("type") || "").toUpperCase();
    const id = searchParams.get("id");

    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [error, setError] = useState("");

    const [adjusting, setAdjusting] = useState(false);
    const [adjustForm, setAdjustForm] = useState(ADJUST_INITIAL);
    const [lots, setLots] = useState([]);
    const [lotsLoading, setLotsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [adjustError, setAdjustError] = useState("");
    const [adjustSuccess, setAdjustSuccess] = useState("");

    const meta = TYPE_META[type];
    const isRM = type === "RM";

    const refresh = useCallback(() => {
        if (!meta || !id) {
            setLoading(false);
            setNotFound(true);
            return;
        }
        setLoading(true);
        setNotFound(false);
        setError("");
        meta
            .fetch(id)
            .then((res) => setItem(res.data))
            .catch((err) => {
                if (err.response?.status === 404) setNotFound(true);
                else setError("Failed to load this item.");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, id]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const startAdjusting = () => {
        setAdjustError("");
        setAdjustSuccess("");
        setAdjustForm(ADJUST_INITIAL);
        setAdjusting(true);
        if (isRM) {
            setLotsLoading(true);
            InventoryService.getLots({ material: item.id, status: "active", page_size: 200 })
                .then((res) => {
                    const d = res.data;
                    const list = Array.isArray(d.results) ? d.results : Array.isArray(d) ? d : [];
                    setLots(list);
                    if (list.length === 1) setAdjustForm((f) => ({ ...f, lot: list[0].id }));
                })
                .catch(() => setLots([]))
                .finally(() => setLotsLoading(false));
        } else {
            setLots([]);
        }
    };

    const selectedLot = lots.find((l) => l.id === adjustForm.lot);
    const canSubmit =
        adjustForm.quantity && parseFloat(adjustForm.quantity) > 0 && (!isRM || adjustForm.lot);

    const handleSubmit = () => {
        setAdjustError("");
        setSaving(true);

        const request = isRM
            ? InventoryService.createTransaction({
                  material: item.id,
                  lot: adjustForm.lot,
                  transaction_type: "adjust",
                  adjustment_direction: adjustForm.direction,
                  quantity: adjustForm.quantity,
                  reference: "Manual Adjustment",
                  notes: adjustForm.notes,
              })
            : MaterialService.adjustAssemblyStock(item.id, {
                  direction: adjustForm.direction,
                  quantity: adjustForm.quantity,
                  notes: adjustForm.notes,
              });

        request
            .then(() => {
                setAdjusting(false);
                setAdjustSuccess("Stock adjustment saved.");
                refresh();
            })
            .catch((err) => {
                const data = err?.response?.data;
                if (data && typeof data === "object") {
                    const first = Object.entries(data)[0];
                    setAdjustError(first ? `${first[0]}: ${first[1]}` : "An error occurred.");
                } else {
                    setAdjustError("An error occurred.");
                }
            })
            .finally(() => setSaving(false));
    };

    return (
        <Box sx={{ p: 2, maxWidth: 520, mx: "auto" }}>
            <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: "divider", borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <MaterialIcon /> {meta?.label || "Material Lookup"}
                </Typography>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : notFound ? (
                    <Alert severity="warning">That item couldn't be found.</Alert>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                        <Box>
                            <Typography variant="h5" fontWeight={700}>
                                {item.full_part_number ?? item.part_number}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {item.description}
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={1}>
                            <StatusChip status={item.status} />
                            {item.category_detail && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`${item.category_detail.code} — ${item.category_detail.name}`}
                                />
                            )}
                        </Stack>

                        <Divider />

                        <Stack spacing={0.75}>
                            <Row label="UOM" value={item.uom_detail?.abbreviation} />
                            <Row label="Qty On Hand" value={item.on_hand_qty ?? item.quantity_on_hand} />
                            {isRM && <Row label="On Order" value={Number(item.on_order_qty ?? 0).toLocaleString()} />}
                            <Row label="Storage Location" value={item.storage_location} />
                            <Row label="Notes" value={item.notes} />
                        </Stack>

                        <Divider />

                        {adjustSuccess && !adjusting && (
                            <Alert severity="success" onClose={() => setAdjustSuccess("")}>
                                {adjustSuccess}
                            </Alert>
                        )}

                        {!adjusting ? (
                            <Button
                                variant="outlined"
                                startIcon={<AdjustIcon />}
                                onClick={startAdjusting}
                            >
                                Adjust Stock
                            </Button>
                        ) : (
                            <Stack spacing={1.5}>
                                <Typography variant="subtitle2" fontWeight={700}>
                                    Adjust Stock
                                </Typography>

                                {adjustError && <Alert severity="error">{adjustError}</Alert>}

                                {isRM && (
                                    <FormControl fullWidth size="small" required>
                                        <InputLabel>Lot</InputLabel>
                                        <Select
                                            value={adjustForm.lot}
                                            label="Lot"
                                            onChange={(e) =>
                                                setAdjustForm((f) => ({ ...f, lot: e.target.value }))
                                            }
                                            disabled={lotsLoading}
                                        >
                                            {lots.map((l) => (
                                                <MenuItem key={l.id} value={l.id}>
                                                    {l.lot_number} — {Number(l.qty_on_hand).toLocaleString()} on hand
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {!lotsLoading && lots.length === 0 && (
                                            <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
                                                No active lots for this material.
                                            </Typography>
                                        )}
                                    </FormControl>
                                )}

                                <ToggleButtonGroup
                                    exclusive
                                    fullWidth
                                    size="small"
                                    value={adjustForm.direction}
                                    onChange={(_, v) =>
                                        v && setAdjustForm((f) => ({ ...f, direction: v }))
                                    }
                                >
                                    <ToggleButton value="in" color="success">
                                        <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> Add
                                    </ToggleButton>
                                    <ToggleButton value="out" color="error">
                                        <RemoveIcon fontSize="small" sx={{ mr: 0.5 }} /> Subtract
                                    </ToggleButton>
                                </ToggleButtonGroup>

                                <TextField
                                    label="Quantity"
                                    size="small"
                                    type="number"
                                    required
                                    value={adjustForm.quantity}
                                    onChange={(e) =>
                                        setAdjustForm((f) => ({ ...f, quantity: e.target.value }))
                                    }
                                    inputProps={{ min: 0.0001, step: "any" }}
                                    helperText={
                                        isRM && selectedLot
                                            ? `Lot on hand after: ${(
                                                  Number(selectedLot.qty_on_hand) +
                                                  (adjustForm.direction === "in" ? 1 : -1) *
                                                      (parseFloat(adjustForm.quantity) || 0)
                                              ).toLocaleString()}`
                                            : " "
                                    }
                                />

                                <TextField
                                    label="Reason / Notes"
                                    size="small"
                                    multiline
                                    minRows={2}
                                    placeholder="e.g. Cycle count adjustment — physical audit found short"
                                    value={adjustForm.notes}
                                    onChange={(e) =>
                                        setAdjustForm((f) => ({ ...f, notes: e.target.value }))
                                    }
                                />

                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="contained"
                                        disabled={saving || !canSubmit}
                                        onClick={handleSubmit}
                                        sx={{ flex: 1 }}
                                    >
                                        {saving ? <CircularProgress size={20} /> : "Save Adjustment"}
                                    </Button>
                                    <Button disabled={saving} onClick={() => setAdjusting(false)}>
                                        Cancel
                                    </Button>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                )}
            </Paper>
        </Box>
    );
}

function Row({ label, value }) {
    if (value === undefined || value === null || value === "") return null;
    return (
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ textAlign: "right" }}>
                {value}
            </Typography>
        </Box>
    );
}
